import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import Swal from 'sweetalert2';
import { ApiService } from '../services/api.service';
import { UtilitiesService } from '../services/utilities.service';

interface StaffPerson {
  id: string;
  name: string;
  username: string | null;
  phone: string | null;
  role: string | null;
  status: string | null;
  hidden: boolean;
}

@Component({
  selector: 'app-staff-settings',
  templateUrl: './staff-settings.component.html',
  styleUrls: ['./staff-settings.component.css'],
  standalone: false
})
export class StaffSettingsComponent implements OnInit {
  token: string | null;
  people: StaffPerson[] = [];
  loadError = false;
  savingId: string | null = null;
  searchTerm = '';
  view: 'all' | 'shown' | 'hidden' = 'all';
  page = 1;
  pageSize = 10;

  constructor(
    public utility: UtilitiesService,
    private api: ApiService,
    private titleService: Title
  ) {
    this.utility.show = true;
    this.token = localStorage.getItem('access_token');
  }

  ngOnInit(): void {
    this.titleService.setTitle('Settings');
    this.load();
  }

  get counts() {
    return {
      all: this.people.length,
      shown: this.people.filter(person => !person.hidden).length,
      hidden: this.people.filter(person => person.hidden).length
    };
  }

  get filteredPeople(): StaffPerson[] {
    const query = this.searchTerm.trim().toLowerCase();
    return this.people
      .filter(person => {
        if (this.view === 'shown' && person.hidden) return false;
        if (this.view === 'hidden' && !person.hidden) return false;
        if (!query) return true;
        return [person.name, person.username, person.phone, person.role, person.status]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(query);
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  get paginatedPeople(): StaffPerson[] {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredPeople.slice(start, start + this.pageSize);
  }

  load(): void {
    this.utility.loading = true;
    this.loadError = false;
    this.api.get('employees/visibility', this.token).subscribe({
      next: (data: unknown) => {
        const rows = Array.isArray(data) ? data : [];
        this.people = rows
          .map(row => this.normalize(row))
          .filter(person => person.id);
        const maxPage = Math.max(1, Math.ceil(this.filteredPeople.length / this.pageSize));
        if (this.page > maxPage) {
          this.page = 1;
        }
      },
      error: () => {
        this.people = [];
        this.loadError = true;
      },
      complete: () => {
        this.utility.loading = false;
      }
    });
  }

  onFilterChange(): void {
    this.page = 1;
  }

  showView(view: 'all' | 'shown' | 'hidden'): void {
    this.view = view;
    this.page = 1;
  }

  toggle(person: StaffPerson, event: Event): void {
    const input = event.target as HTMLInputElement;
    const hidden = !input.checked;
    if (this.savingId) {
      input.checked = !person.hidden;
      return;
    }

    this.savingId = person.id;
    this.api.update(`employees/${person.id}/hidden`, { hidden }, this.token).subscribe({
      next: () => {
        person.hidden = hidden;
      },
      error: () => {
        input.checked = !person.hidden;
        Swal.fire({
          title: 'Error!',
          text: 'Could not update this person.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      },
      complete: () => {
        this.savingId = null;
      }
    });
  }

  statusLabel(status: string | null): string {
    switch ((status || '').toUpperCase()) {
      case 'ACTIVE':
        return 'Active';
      case 'INACTIVE':
        return 'Inactive';
      case 'LEAVE':
        return 'On leave';
      case 'TERMINATED':
        return 'Terminated';
      case 'ESCAPE':
        return 'Escape';
      default:
        return status || 'Unknown';
    }
  }

  private normalize(row: any): StaffPerson {
    const source = row?.employee ?? row ?? {};
    return {
      id: source.id != null ? String(source.id) : '',
      name: source.name ?? '',
      username: source.username ?? null,
      phone: source.phone != null ? String(source.phone) : null,
      role: source.role ?? null,
      status: source.status ?? null,
      hidden: source.hidden === true || source.hidden === 1 || source.hidden === '1' || source.hidden === 'true'
    };
  }
}
